import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [managerProfile, setManagerProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          await loadProfile(session.user.id);
        } else {
          setUser(null);
          setCompany(null);
          setIsAdmin(false);
          setIsManager(false);
          setManagerProfile(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId) => {
    try {
      // ── Step 1: Check admin ──────────────────────
      const { data: adminData } = await supabase
        .from('admin_users')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (adminData) {
        setIsAdmin(true);
        setIsManager(false);
        setManagerProfile(null);
        setCompany(null);
        setLoading(false);
        return;
      }

      // ── Step 2: Check manager ────────────────────
      const { data: managerData } = await supabase
        .from('managers')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (managerData) {
        setIsManager(true);
        setManagerProfile(managerData);
        setIsAdmin(false);
        setCompany(null);
        setLoading(false);
        return;
      }

      // ── Step 3: Check company ────────────────────
      const { data: companyData } = await supabase
        .from('companies')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();

      setCompany(companyData ?? null);
      setIsAdmin(false);
      setIsManager(false);
      setManagerProfile(null);
    } catch (err) {
      console.error('loadProfile error:', err);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCompany(null);
    setIsAdmin(false);
    setIsManager(false);
    setManagerProfile(null);
  };

  const refreshCompany = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      setCompany(data ?? null);
    } catch (err) {
      console.error('refreshCompany error:', err);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      company,
      isAdmin,
      isManager,
      managerProfile,
      loading,
      signOut,
      refreshCompany,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);